/**
 * Where a library clip's BYTES come from — ADR-033 phase 4, the app half.
 *
 * The library read (phase 3) says which clips exist and whether prod ships
 * them; this seam says where to get one to play:
 *  - a clip the bake folder carries → the folder's own `<group>/<clipId>.glb`
 *    (the pool the author pins from, even before World deploys it);
 *  - else, when prod ships it → World's URL rule, ADR-178 §3c:
 *    `<base>/animations/<family>/<group>/<clipId>.glb`, same origin;
 *  - else a refusal that says so: a STAGED clip with no folder bytes cannot
 *    be previewed here, and the panel must not pretend otherwise.
 *
 * Depends on: library-read (types), kernel (types). Depended on by: main's
 * presentation door, the package panel, tests.
 */
import type { ClipBadge } from '@harryhausen/kernel';
import type { LibraryForRig, LibraryRead } from './library-read.js';

export type FetchBytes = (url: string) => Promise<ArrayBuffer>;

/** World's clip URL (ADR-178 §3c). */
export function libraryClipUrl(base: string, familyId: string, group: string, clipId: string): string {
  return `${base}/animations/${familyId}/${group}/${clipId}.glb`;
}

/** A fetch that refuses non-2xx loudly and names the URL — the reader semantics the library read uses. */
export function fetchBytesStrict(fetchFn: (url: string) => Promise<{ ok: boolean; status: number; arrayBuffer(): Promise<ArrayBuffer> }>): FetchBytes {
  return async (url) => {
    let res: { ok: boolean; status: number; arrayBuffer(): Promise<ArrayBuffer> };
    try {
      res = await fetchFn(url);
    } catch (e) {
      throw new Error(`${url}: ${e instanceof Error ? e.message : String(e)}`);
    }
    if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
    try {
      // A body that fails mid-stream (a 2xx, then the connection drops) is a failure of THIS URL too (Codex P2, #151 round three).
      return await res.arrayBuffer();
    } catch (e) {
      throw new Error(`${url}: ${e instanceof Error ? e.message : String(e)}`);
    }
  };
}

export type ClipBytesSource = { readonly kind: 'folder'; readonly name: string } | { readonly kind: 'prod'; readonly url: string };

export interface ResolvedClipBytes {
  readonly clipId: string;
  readonly bytes: ArrayBuffer;
  readonly source: ClipBytesSource;
}

export interface ResolveClipBytesInputs {
  readonly read: LibraryRead;
  readonly rig: LibraryForRig;
  readonly clipId: string;
  readonly base: string;
  readonly fetchBytes: FetchBytes;
}

/** The badge for a clipId in the rig's pool, or null when the pool does not carry it. */
export function badgeFor(rig: LibraryForRig, clipId: string): ClipBadge | null {
  return rig.badged?.clips.find((c) => c.clipId === clipId) ?? null;
}

/**
 * Bytes for one clip of the rig's pool: the folder first, prod when shipped,
 * a refusal otherwise. Every refusal names the clip and the reason.
 */
export async function resolveClipBytes(inputs: ResolveClipBytesInputs): Promise<ResolvedClipBytes> {
  const { read, rig, clipId } = inputs;
  if (rig.detection.family === null || rig.badged === null) {
    throw new Error(`no library pool for this rig — ${rig.reasons[0] ?? 'the family match failed'}`);
  }
  const badge = badgeFor(rig, clipId);
  if (badge === null) {
    throw new Error(`"${clipId}" is not in the ${rig.badged.familyId} pool (${rig.badged.bakeVersion})`);
  }
  const familyId = rig.detection.family.id;
  const folder = read.bake !== null && read.bake.manifest.family.id === familyId ? read.bake : null;
  if (folder !== null && folder.clipBytes !== undefined) {
    const bytes = await folder.clipBytes(badge.group, clipId);
    if (bytes !== null) return { clipId, bytes, source: { kind: 'folder', name: folder.name } };
  }
  if (badge.shipping === 'shipped') {
    // Prod's bytes live under PROD's group for this id: `shipped` is a match
    // by id alone, and a bake may have re-grouped the clip (Codex P2, #151
    // round four) — asking prod for the bake's group would 404 a clip prod
    // carries.
    const prodRecord = read.prod.find((p) => p.id === familyId)?.manifest?.family.clips.find((c) => c.clipId === clipId);
    const url = libraryClipUrl(inputs.base, familyId, prodRecord?.group ?? badge.group, clipId);
    return { clipId, bytes: await inputs.fetchBytes(url), source: { kind: 'prod', url } };
  }
  throw new Error(
    folder === null
      ? `"${clipId}" is staged, not shipped — prod has no bytes for it and no bake folder is open; open the bake folder to preview it`
      : `"${clipId}" is staged, not shipped, and the bake folder "${folder.name}" has no ${badge.group}/${clipId}.glb`,
  );
}
